import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
   HiOutlineCurrencyDollar,
   HiOutlinePresentationChartLine,
   HiOutlineChartPie,
   HiOutlineTableCells,
   HiOutlineChevronDown,
   HiOutlineChevronUp,
   HiOutlineArrowLeft,
   HiOutlineAdjustmentsHorizontal,
   HiOutlineCalendar
} from "react-icons/hi2";
import {
   ComposedChart,
   BarChart,
   Bar,
   LineChart,
   Line,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   ResponsiveContainer,
   Cell,
   PieChart,
   Pie,
   Legend,
} from "recharts";
import axios from "axios";
import ReportFilterBar from "../../components/ReportFilterBar";
import "../Reports.css";
import apiClient from "../../config/api";
import {
  showReportGeneratingToast,
  reportGenerationDone,
  reportGenerationFailed,
} from "../../utils/reportGenerationToast";

import { SEMANTIC_SUCCESS_MID } from '../../theme/chartColors';
const COLORS = {
   Availability: "#f59e0b",
   Performance: "#3b82f6",
   Quality: SEMANTIC_SUCCESS_MID,
   "Start-up Loss": "#f59e0b",
   "Breakdown Loss": "#ef4444",
   "Changeover Loss": "#8b5cf6",
   "Performance Loss": "#3b82f6",
   "Quality Loss": SEMANTIC_SUCCESS_MID,
};

const formatRs = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val);
const formatNum = (val) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 1 }).format(val);

const OeeFinancialLossReport = () => {
   const navigate = useNavigate();
   const [showFilters, setShowFilters] = useState(true);
   const [viewMode, setViewMode] = useState("chart"); // "chart" | "table"
   const [loading, setLoading] = useState(false);
   const [reportData, setReportData] = useState(null);
   const [error, setError] = useState("");

   const [selectedPlants, setSelectedPlants] = useState([]);
   const [selectedLines, setSelectedLines] = useState([]);
   const [selectedMachines, setSelectedMachines] = useState([]);
   const [selectedShifts, setSelectedShifts] = useState([]);
   const [selectedProducts, setSelectedProducts] = useState([]);
   const [selectedSkus, setSelectedSkus] = useState([]);
   const [startDate, setStartDate] = useState("");
   const [endDate, setEndDate] = useState("");

   const activeFilterCount =
      selectedPlants.length +
      selectedLines.length +
      selectedMachines.length +
      selectedShifts.length +
      selectedProducts.length +
      selectedSkus.length +
      (startDate ? 1 : 0) +
      (endDate ? 1 : 0);

   const handleGenerate = async () => {
      if (!startDate || !endDate) {
         setError("Please select both Start Date and End Date.");
         return;
      }
      setError("");
      showReportGeneratingToast();
      setLoading(true);

      try {
         const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
         const headers = token ? { Authorization: `Bearer ${token}` } : {};
         const params = {
            startDate,
            endDate,
            ...(selectedPlants.length > 0 && { plantId: selectedPlants[0] }),
            ...(selectedLines.length > 0 && { lineId: selectedLines[0] }),
            ...(selectedMachines.length > 0 && { machineId: selectedMachines[0] }),
            ...(selectedShifts.length > 0 && { shift: selectedShifts[0] }),
            ...(selectedProducts.length > 0 && { productId: selectedProducts[0] }),
            ...(selectedSkus.length > 0 && { skuId: selectedSkus[0] }),
         };

         // Fetch both financial limits and pure output units
         const [finRes, sumRes] = await Promise.all([
            apiClient.get("/reports/oee-financial-loss", { params, headers }),
            apiClient.get("/reports/oee-summary", { params, headers }),
         ]);

         const finData = finRes.data || [];
         const sumData = sumRes.data || [];

         // Combine Data
         const processed = finData.map((fin) => {
            const summaryMatch = sumData.find(
               (s) =>
                  s.date === fin.date &&
                  s.machineName === fin.machineName &&
                  s.shift === fin.shift
            );

            const costPrice = fin.costPrice || 0;
            const totalQty = summaryMatch ? summaryMatch.totalQty : 0;
            const goodQty = summaryMatch ? summaryMatch.goodQty : 0;
            const rejectQty = summaryMatch ? summaryMatch.rejectQty : 0;
            const reworkQty = summaryMatch ? summaryMatch.reworkQty : 0;

            const availabilityLossMin =
               fin.startupLossMinutes +
               fin.breakdownLossMinutes +
               fin.changeoverLossMinutes;
            const availabilityLossRs =
               fin.startupLossFinancial +
               fin.breakdownLossFinancial +
               fin.changeoverLossFinancial;
            const performanceLossRs = fin.speedLossFinancial;
            const qualityLossRs = fin.qualityLossFinancial;
            const totalLossRs = fin.totalFinancialLoss;

            let performanceLossUnits = 0;
            if (costPrice > 0) {
               performanceLossUnits = performanceLossRs / costPrice;
            }

            const qualityLossUnits = rejectQty + reworkQty;
            const lostUnits = performanceLossUnits + qualityLossUnits;
            const idealOutput = totalQty + performanceLossUnits;
            const actualOutput = totalQty;

            const actualRevenue = goodQty * costPrice;
            const potentialRevenue = idealOutput * costPrice; // Max achievable rev

            return {
               ...fin,
               availabilityLossMin,
               availabilityLossRs,
               performanceLossRs,
               qualityLossRs,
               totalLossRs,
               actualOutput,
               idealOutput,
               lostUnits,
               performanceLossUnits,
               qualityLossUnits,
               actualRevenue,
               potentialRevenue,
               costPrice,
               rejectQty,
               reworkQty,
               productName: fin.productName || fin.skuName || "Unknown Product",
            };
         });

         setReportData(processed);
         reportGenerationDone();
      } catch (err) {
         console.error(err);
         const msg = "Failed to fetch reports. Please try again.";
         setError(msg);
         reportGenerationFailed(msg);
      } finally {
         setLoading(false);
      }
   };

   const clearAllFilters = () => {
      setSelectedPlants([]);
      setSelectedLines([]);
      setSelectedMachines([]);
      setSelectedShifts([]);
      setSelectedProducts([]);
      setSelectedSkus([]);
      setStartDate("");
      setEndDate("");
      setReportData(null);
      setError("");
   };

   const aggs = useMemo(() => {
      if (!reportData) return null;
      let totFinancial = 0,
         availRs = 0,
         perfRs = 0,
         qualRs = 0;
      let totalActualRev = 0,
         totalPotRev = 0;

      let lossTypeData = [
         { name: "Start-up Loss", min: 0, units: 0, rs: 0 },
         { name: "Breakdown Loss", min: 0, units: 0, rs: 0 },
         { name: "Changeover Loss", min: 0, units: 0, rs: 0 },
         { name: "Performance Loss", min: 0, units: 0, rs: 0 },
         { name: "Quality Loss", min: 0, units: 0, rs: 0 },
      ];

      let machineDataMap = {};
      let productDataMap = {};
      let dailyDataMap = {};
      let breakdownTableAggs = {};

      reportData.forEach((d) => {
         totFinancial += d.totalLossRs;
         availRs += d.availabilityLossRs;
         perfRs += d.performanceLossRs;
         qualRs += d.qualityLossRs;
         totalActualRev += d.actualRevenue;
         totalPotRev += d.potentialRevenue;

         lossTypeData[0].min += d.startupLossMinutes;
         lossTypeData[0].rs += d.startupLossFinancial;

         lossTypeData[1].min += d.breakdownLossMinutes;
         lossTypeData[1].rs += d.breakdownLossFinancial;

         lossTypeData[2].min += d.changeoverLossMinutes;
         lossTypeData[2].rs += d.changeoverLossFinancial;

         lossTypeData[3].min += d.speedLossMinutes;
         lossTypeData[3].rs += d.performanceLossRs;
         lossTypeData[3].units += d.performanceLossUnits;

         lossTypeData[4].min += d.qualityLossMinutes;
         lossTypeData[4].rs += d.qualityLossRs;
         lossTypeData[4].units += d.qualityLossUnits;

         if (d.costPrice > 0) {
            lossTypeData[0].units += d.startupLossFinancial / d.costPrice;
            lossTypeData[1].units += d.breakdownLossFinancial / d.costPrice;
            lossTypeData[2].units += d.changeoverLossFinancial / d.costPrice;
         }

         // Machine Bar Chart
         if (!machineDataMap[d.machineName]) {
            machineDataMap[d.machineName] = { name: d.machineName, loss: 0 };
         }
         machineDataMap[d.machineName].loss += d.totalLossRs;

         // Product Table
         const prodCode = d.productName;
         if (!productDataMap[prodCode]) {
            productDataMap[prodCode] = {
               name: prodCode,
               idealOut: 0,
               actOut: 0,
               lostUnits: 0,
               rs: 0,
               costPrices: [],
            };
         }
         productDataMap[prodCode].idealOut += d.idealOutput;
         productDataMap[prodCode].actOut += d.actualOutput;
         productDataMap[prodCode].lostUnits += d.lostUnits;
         productDataMap[prodCode].rs += d.totalLossRs;
         if (!productDataMap[prodCode].costPrices.includes(d.costPrice))
            productDataMap[prodCode].costPrices.push(d.costPrice);

         // Breakdown Table Grid
         const mgKey = `${d.machineName}_${prodCode}`;
         if (!breakdownTableAggs[mgKey]) {
            breakdownTableAggs[mgKey] = {
               machineName: d.machineName,
               productName: prodCode,
               availabilityLossRs: 0,
               performanceLossRs: 0,
               qualityLossRs: 0,
               totalLossRs: 0,
            };
         }
         breakdownTableAggs[mgKey].availabilityLossRs += d.availabilityLossRs;
         breakdownTableAggs[mgKey].performanceLossRs += d.performanceLossRs;
         breakdownTableAggs[mgKey].qualityLossRs += d.qualityLossRs;
         breakdownTableAggs[mgKey].totalLossRs += d.totalLossRs;

         // Daily Trend
         if (!dailyDataMap[d.date]) {
            dailyDataMap[d.date] = { date: d.date, loss: 0 };
         }
         dailyDataMap[d.date].loss += d.totalLossRs;
      });

      lossTypeData.forEach(lt => {
         lt.pct = totFinancial > 0 ? (lt.rs / totFinancial) * 100 : 0;
      });
      lossTypeData.sort((a, b) => b.rs - a.rs);

      let cumPop = 0;
      const pareto = lossTypeData.map(lt => {
         cumPop += lt.pct;
         return { ...lt, cumPct: cumPop };
      });

      return {
         totFinancial,
         availRs,
         perfRs,
         qualRs,
         totalActualRev,
         totalPotRev,
         lossTypeData,
         pareto,
         machineChart: Object.values(machineDataMap).sort((a, b) => b.loss - a.loss),
         prodTable: Object.values(productDataMap).sort((a, b) => b.rs - a.rs),
         breakdownTable: Object.values(breakdownTableAggs).sort((a, b) => b.totalLossRs - a.totalLossRs),
         dailyChart: Object.values(dailyDataMap).sort((a, b) => new Date(a.date) - new Date(b.date)),
         pieData: [
            { name: "Availability Loss", value: availRs, color: COLORS.Availability },
            { name: "Performance Loss", value: perfRs, color: COLORS.Performance },
            { name: "Quality Loss", value: qualRs, color: COLORS.Quality }
         ].filter(x => x.value > 0)
      };
   }, [reportData]);

   // UI Components
   const highestLossCategory = useMemo(() => {
      if (!aggs) return "";
      if (aggs.availRs >= aggs.perfRs && aggs.availRs >= aggs.qualRs) return "Availability Loss";
      if (aggs.perfRs >= aggs.availRs && aggs.perfRs >= aggs.qualRs) return "Performance Loss";
      return "Quality Loss";
   }, [aggs]);

   return (
      <div className="pagecontainer">
         <div className="page-header">
            <div className="oee-report-header-left">
               <button
                  onClick={() => navigate("/home/reports")}
                  className="oee-report-back-btn mr-2"
                  title="Back to OEE Reports"
               >
                  <HiOutlineArrowLeft size={18} />
               </button>
               <div>
                  <h1 className="oee-report-title">
                     <HiOutlinePresentationChartLine />
                     OEE Financial Loss Dashboard
                  </h1>
                  <p className="oee-report-subtitle">
                     Analyze occurrences and frequency trends over time
                  </p>
               </div>
            </div>
            <div className="header-actions">
               <button onClick={() => setShowFilters(!showFilters)}
                  className="oee-filter-toggle-btn"
               >
                  <HiOutlineAdjustmentsHorizontal size={16} />
                  Filters
                  {activeFilterCount > 0 && (
                     <span className="oee-filter-badge">{activeFilterCount}</span>
                  )}
                  {showFilters ? (
                     <HiOutlineChevronUp size={13} />
                  ) : (
                     <HiOutlineChevronDown size={13} />
                  )}
               </button>
            </div>
         </div>
         {/* FILTER SECTION */}
         <div className="reportbox searchfilterbox"
         >
            {/* <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showFilters ? "16px" : "0" }}>
               <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <button
                     onClick={() => navigate("/home/reports")}
                     className="oee-report-back-btn"
                     title="Back to OEE Reports"
                  >
                     <HiOutlineArrowLeft size={18} />
                  </button>
                  <h2 style={{ fontSize: "18px", color: "#1e293b", margin: 0 }}></h2>
               </div>
               <button onClick={() => setShowFilters(!showFilters)} style={{ border: "none", background: "none", color: "#2b394d", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
                  {showFilters ? <HiOutlineChevronUp size={20} /> : <HiOutlineChevronDown size={20} />} Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
               </button>
            </div> */}

            {showFilters && (
               <>
                  <div className="report-filters">
                     <div className="filter-group">
                        <label>Start Date <span className="report-required">*</span></label>
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="filter-input" />
                     </div>
                     <div className="filter-group">
                        <label>End Date <span className="report-required">*</span></label>
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="filter-input" />
                     </div>
                     <ReportFilterBar
                        selectedPlants={selectedPlants} onPlantChange={setSelectedPlants}
                        selectedLines={selectedLines} onLineChange={setSelectedLines}
                        selectedMachines={selectedMachines} onMachineChange={setSelectedMachines}
                        selectedShifts={selectedShifts} onShiftChange={setSelectedShifts}
                        selectedProducts={selectedProducts} onProductChange={setSelectedProducts}
                        selectedSkus={selectedSkus} onSkuChange={setSelectedSkus}
                     />
                  </div>

                  <div className="report-filter-actions">
                     {activeFilterCount > 0 && (
                        <button className="oee-clear-filters-btn" onClick={clearAllFilters}>
                           ✕ Clear Filters
                        </button>
                     )}
                     <button type="button" className="btn-primary" onClick={handleGenerate} disabled={loading}>
                        <HiOutlineCalendar /> Generate Dashboard
                     </button>
                  </div>
               </>
            )}
         </div>

         {reportData && aggs && (
            <div className="report-stack">

               {/* KPI CARDS */}
               <div className="report-kpi-grid">

                  <div
                     className="report-card report-card--accent-left"
                     style={{ ["--report-accent"]: "var(--status-error-text)" }}
                  >
                     <p className="report-card__kpi-label">Total Financial Loss</p>
                     <h2 className="report-card__kpi-value">{formatRs(aggs.totFinancial)}</h2>
                  </div>

                  <div
                     className={`report-card report-card--accent-left ${highestLossCategory === "Availability Loss" ? "report-card--highlight-warm" : ""}`}
                     style={{ ["--report-accent"]: COLORS.Availability }}
                  >
                     <p className="report-card__kpi-label">Availability Loss Cost</p>
                     <div className="report-card__kpi-row">
                        <h2 className="report-card__kpi-value report-card__kpi-value--md">{formatRs(aggs.availRs)}</h2>
                        {highestLossCategory === "Availability Loss" && <span className="report-badge--danger">Highest Loss</span>}
                     </div>
                  </div>

                  <div
                     className={`report-card report-card--accent-left ${highestLossCategory === "Performance Loss" ? "report-card--highlight-info" : ""}`}
                     style={{ ["--report-accent"]: COLORS.Performance }}
                  >
                     <p className="report-card__kpi-label">Performance Loss Cost</p>
                     <div className="report-card__kpi-row">
                        <h2 className="report-card__kpi-value report-card__kpi-value--md">{formatRs(aggs.perfRs)}</h2>
                        {highestLossCategory === "Performance Loss" && <span className="report-badge--danger">Highest Loss</span>}
                     </div>
                  </div>

                  <div
                     className={`report-card report-card--accent-left ${highestLossCategory === "Quality Loss" ? "report-card--highlight-success" : ""}`}
                     style={{ ["--report-accent"]: COLORS.Quality }}
                  >
                     <p className="report-card__kpi-label">Quality Loss Cost</p>
                     <div className="report-card__kpi-row">
                        <h2 className="report-card__kpi-value report-card__kpi-value--md">{formatRs(aggs.qualRs)}</h2>
                        {highestLossCategory === "Quality Loss" && <span className="report-badge--danger">Highest Loss</span>}
                     </div>
                  </div>

                  <div className="report-card report-card--emphasis">
                     <p className="report-card__kpi-label">Actual / Potential Rev</p>
                     <div className="report-card__kpi-row">
                        <h2 className="report-card__kpi-value report-card__kpi-value--sm">
                           {formatRs(aggs.totalActualRev)} <span className="report-card__sep">/</span>
                        </h2>
                        <h3 className="report-card__kpi-sub">{formatRs(aggs.totalPotRev)}</h3>
                     </div>
                  </div>

               </div>

               {/* VIEW MODE TOGGLE */}
               <div className="viewTypeheader" >
                  <button
                     onClick={() => setViewMode("chart")}
                     className={viewMode === "chart" ? "btn btn-primary mr-2" : "btn btn-secondary mr-2"}
                  // style={{
                  //    display: "flex", alignItems: "center", gap: "6px",
                  //    padding: "8px 16px", borderRadius: "8px", border: "1px solid #e2e8f0; border-color: var(--border-primary)",
                  //    fontWeight: 600, fontSize: "14px", cursor: "pointer", transition: "all 0.2s",
                  //    backgroundColor: viewMode === "chart" ? "#4f46e5" : "#fff",
                  //    color: viewMode === "chart" ? "#fff" : "#2b394d"
                  // }}
                  >
                     <HiOutlinePresentationChartLine size={18} /> Charts View
                  </button>
                  <button
                     onClick={() => setViewMode("table")}
                     className={viewMode === "table" ? "btn btn-primary" : "btn btn-secondary"}
                  // style={{
                  //    display: "flex", alignItems: "center", gap: "6px",
                  //    padding: "8px 16px", borderRadius: "8px", border: "1px solid #e2e8f0; border-color: var(--border-primary)",
                  //    fontWeight: 600, fontSize: "14px", cursor: "pointer", transition: "all 0.2s",
                  //    backgroundColor: viewMode === "table" ? "#4f46e5" : "#fff",
                  //    color: viewMode === "table" ? "#fff" : "#2b394d"
                  // }}
                  >
                     <HiOutlineTableCells size={18} /> Tables View
                  </button>
               </div>

               {/* CHARTS GRID */}
               {viewMode === "chart" && (
                  <div className="report-charts-grid">
                     {/* Pareto */}
                     <div className="report-card report-card--pad-lg">
                        <h3 className="report-card__title">Financial Loss Pareto</h3>
                        <ResponsiveContainer width="100%" height={300}>
                           <ComposedChart data={aggs.pareto} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={60} />
                              <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v / 1000}k`} />
                              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                              <Tooltip formatter={(val, name) => name === "Cumulative %" ? `${formatNum(val)}%` : formatRs(val)} />
                              <Bar yAxisId="left" dataKey="rs" name="Loss (₹)" radius={[4, 4, 0, 0]}>
                                 {aggs.pareto.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[entry.name] || "var(--text-tertiary)"} />
                                 ))}
                              </Bar>
                              <Line yAxisId="right" type="monotone" dataKey="cumPct" name="Cumulative %" stroke="var(--text-tertiary)" strokeWidth={2} dot={{ r: 3 }} />
                           </ComposedChart>
                        </ResponsiveContainer>
                     </div>

                     {/* Machine Bar */}
                     <div className="report-card report-card--pad-lg">
                        <h3 className="report-card__title">Machine-wise Financial Loss</h3>
                        <ResponsiveContainer width="100%" height={300}>
                           <BarChart data={aggs.machineChart} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v / 1000}k`} />
                              <Tooltip formatter={(val) => formatRs(val)} />
                              <Bar dataKey="loss" name="Loss (₹)" fill="var(--status-info-text)" radius={[4, 4, 0, 0]} maxBarSize={60} />
                           </BarChart>
                        </ResponsiveContainer>
                     </div>

                     {/* Loss Distribution Pie */}
                     <div className="report-card report-card--pad-lg">
                        <h3 className="report-card__title">Loss Distribution (%)</h3>
                        <ResponsiveContainer width="100%" height={300}>
                           <PieChart>
                              <Pie data={aggs.pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`} labelLine={true}>
                                 {aggs.pieData.map((entry, idx) => (
                                    <Cell key={`cell-${idx}`} fill={entry.color} />
                                 ))}
                              </Pie>
                              <Tooltip formatter={(val) => formatRs(val)} />
                           </PieChart>
                        </ResponsiveContainer>
                     </div>

                     {/* Daily Trend */}
                     <div className="report-card report-card--pad-lg">
                        <h3 className="report-card__title">Daily Financial Loss Trend</h3>
                        <ResponsiveContainer width="100%" height={300}>
                           <LineChart data={aggs.dailyChart} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v / 1000}k`} />
                              <Tooltip formatter={(val) => formatRs(val)} />
                              <Line type="monotone" dataKey="loss" name="Total Loss (₹)" stroke="var(--status-error-text)" strokeWidth={3} dot={{ r: 4 }} />
                           </LineChart>
                        </ResponsiveContainer>
                     </div>
                  </div>
               )}

               {/* TABLES */}
               {viewMode === "table" && (
                  <div className="report-stack">

                     {/* Financial Loss Breakdown Table */}
                     <div className="report-table-shell">
                        <div className="report-table-shell__head">
                           <h3>Breakdown by Machine & Product</h3>
                        </div>
                        <div className="report-table-scroll">
                           <table className="report-data-table">
                              <thead>
                                 <tr>
                                    <th>Machine</th>
                                    <th>Product</th>
                                    <th className="report-th-num">Availability Loss</th>
                                    <th className="report-th-num">Performance Loss</th>
                                    <th className="report-th-num">Quality Loss</th>
                                    <th className="report-th-num">Total Loss</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {aggs.breakdownTable.map((row, idx) => (
                                    <tr
                                       key={idx}
                                       className={idx === 0 && row.totalLossRs > 0 ? "report-row--highlight" : undefined}
                                    >
                                       <td className="report-td-strong">{row.machineName}</td>
                                       <td>{row.productName}</td>
                                       <td className="report-td-num">{formatRs(row.availabilityLossRs)}</td>
                                       <td className="report-td-num">{formatRs(row.performanceLossRs)}</td>
                                       <td className="report-td-num">{formatRs(row.qualityLossRs)}</td>
                                       <td className="report-td-num report-td-strong">{formatRs(row.totalLossRs)}</td>
                                    </tr>
                                 ))}
                                 <tr className="report-row-total">
                                    <td colSpan={2} className="report-td-num">Grand Total</td>
                                    <td className="report-td-num">{formatRs(aggs.availRs)}</td>
                                    <td className="report-td-num">{formatRs(aggs.perfRs)}</td>
                                    <td className="report-td-num">{formatRs(aggs.qualRs)}</td>
                                    <td className="report-td-num">{formatRs(aggs.totFinancial)}</td>
                                 </tr>
                              </tbody>
                           </table>
                        </div>
                     </div>

                     {/* Loss Type Financial Table */}
                     <div className="report-table-shell">
                        <div className="report-table-shell__head">
                           <h3>Financial Impact by Loss Type</h3>
                        </div>
                        <div className="report-table-scroll">
                           <table className="report-data-table">
                              <thead>
                                 <tr>
                                    <th>Loss Type</th>
                                    <th className="report-th-num">Time Lost (min)</th>
                                    <th className="report-th-num">Units Lost</th>
                                    <th className="report-th-num">Financial Loss (₹)</th>
                                    <th className="report-th-num">% of Total</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {aggs.lossTypeData.map((row, idx) => (
                                    <tr key={idx}>
                                       <td>
                                          <span className="report-loss-type-cell">
                                             <span
                                                className="report-cell-swatch"
                                                style={{ backgroundColor: COLORS[row.name] || "var(--border-primary)" }}
                                             />
                                             {row.name}
                                          </span>
                                       </td>
                                       <td className="report-td-num">{formatNum(row.min)}</td>
                                       <td className="report-td-num">{formatNum(row.units)}</td>
                                       <td className="report-td-num report-td-strong">{formatRs(row.rs)}</td>
                                       <td className="report-td-num">{formatNum(row.pct)}%</td>
                                    </tr>
                                 ))}
                              </tbody>
                           </table>
                        </div>
                     </div>

                     {/* Product Financial Impact Table */}
                     <div className="report-table-shell">
                        <div className="report-table-shell__head">
                           <h3>Product Revenue & Impact</h3>
                        </div>
                        <div className="report-table-scroll">
                           <table className="report-data-table">
                              <thead>
                                 <tr>
                                    <th>Product</th>
                                    <th className="report-th-num">Ideal Output</th>
                                    <th className="report-th-num">Actual Output</th>
                                    <th className="report-th-num">Lost Units</th>
                                    <th className="report-th-num">Unit Cost (₹)</th>
                                    <th className="report-th-num">Total Loss (₹)</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {aggs.prodTable.map((row, idx) => (
                                    <tr key={idx}>
                                       <td className="report-td-strong">{row.name}</td>
                                       <td className="report-td-num">{formatNum(row.idealOut)}</td>
                                       <td className="report-td-num">{formatNum(row.actOut)}</td>
                                       <td className="report-td-num report-td-danger">{formatNum(row.lostUnits)}</td>
                                       <td className="report-td-num">{row.costPrices.map(c => formatRs(c)).join(', ')}</td>
                                       <td className="report-td-num report-td-strong">{formatRs(row.rs)}</td>
                                    </tr>
                                 ))}
                              </tbody>
                           </table>
                        </div>
                     </div>

                  </div>
               )}
            </div>
         )}
      </div>
   );
};

export default OeeFinancialLossReport;
